import { normalize, schema } from 'normalizr';
import type { Metadata } from '../../components';
import type { Reference, SupportedLanguages } from '../../models';
import { getLanguageValue } from '../../utils/translation-utils';

type NormalizeParams = {
  reference: Reference;
  extendedMetadata: Metadata;
  filteredFieldPath: string;
  metadataPath?: string;
};

const ENTITY_IDENTIFIER_SEPARATOR = '.';
const ENTITY_IDENTIFIER_ATTRIBUTE = '__path';
const ENTITY_PARENT_IDENTIFIER_ATTRIBUTE = '__parent';
const REFERENCE_SEPARATOR = ':';

let languagePriorityOrder = ['pt', 'en', 'es'];
let language = 'en';

export function normalizeMetadata(
  _input: NormalizeParams,
  formLanguage: string,
  supportedLanguages?: SupportedLanguages[],
): any {
  if (supportedLanguages?.length) {
    languagePriorityOrder = supportedLanguages.map((lang) => lang.code);
  }
  const _output = {};
  language = formLanguage;
  const reference = _input.reference;
  const extendedMetadata = _input.extendedMetadata;
  const metadataPath = _input.metadataPath || getMetadataPathFromReference(reference, null);
  const filteredFieldPath = _input.filteredFieldPath;

  extendedMetadata[ENTITY_IDENTIFIER_ATTRIBUTE] = metadataPath;

  const normalizedData = normalizeData(extendedMetadata, metadataPath);

  _output['fields'] = checkIfIsFiltered(normalizedData.entities.fields, filteredFieldPath);
  _output['metadatas'] = normalizedData.entities.metadatas;

  return _output;
}

function checkIfIsFiltered(fields, filteredFieldPath: string) {
  if (!filteredFieldPath?.length) return fields;

  const result = {};
  const paths = Object.keys(fields);
  paths.forEach((path) => {
    const field = fields[path];
    if (checkIfPathIsSubPath(filteredFieldPath, field.__path)) {
      result[path] = field;
    }
  });

  return result;
}
/**
 *  Core functions
 * @returns
 */
function normalizeData(data, basePath) {
  const schema = getMetadataSchema(basePath);
  const fieldSchema = getFieldSchema(schema);
  schema.define({
    fields: [fieldSchema],
    embFields: [fieldSchema],
  });
  return normalize(data, schema);
}

function getMetadataSchema(basePath) {
  return new schema.Entity('metadatas', undefined, {
    idAttribute: ENTITY_IDENTIFIER_ATTRIBUTE,
    // Pre-processing the entity
    processStrategy: (value) => {
      const entityPath = value[ENTITY_IDENTIFIER_ATTRIBUTE] || basePath;
      const ref = getReferenceFromPath(entityPath);
      const fieldAttribute = getFieldAttributeFromPath(entityPath);
      const parentPath = getPathFromFieldAttribute(fieldAttribute, ref);
      const parsedFields = parseFields(value.fields, parentPath, value.__field);
      const parsedMetadata = {
        _id: value._id,
        identifier: value.identifier,
        name: getCurrentTranslationFromI18N(value.name),
        package: value.package,
        concreteClasses: value.concreteClasses,
        implements: value.implements,
        fields: parsedFields,
        type: value.type,
        editHelp: getCurrentTranslationFromI18N(value.editHelp),
      };
      parsedMetadata[ENTITY_IDENTIFIER_ATTRIBUTE] = entityPath;

      return parsedMetadata;
    },
  });
}

function getFieldSchema(metadataSchema) {
  return new schema.Entity(
    'fields',
    {
      embeddedMetadatas: new schema.Values(metadataSchema),
      embeddedMetadata: metadataSchema,
    },
    {
      idAttribute: ENTITY_IDENTIFIER_ATTRIBUTE,
      // Pre-processing the entity
      processStrategy: (value) => {
        const entityData = {};
        const __path = value[ENTITY_IDENTIFIER_ATTRIBUTE];
        entityData[ENTITY_IDENTIFIER_ATTRIBUTE] = __path;
        const embeddedMetadatas = {
          embeddedMetadatas: parseEmbbededMetadatas(value.embeddedMetadatas, value[ENTITY_IDENTIFIER_ATTRIBUTE]),
          embeddedMetadata: parseEmbbededMetadata(value.embeddedMetadata, value[ENTITY_IDENTIFIER_ATTRIBUTE], null),
        };
        const parsedField = Object.assign({}, entityData, value, embeddedMetadatas);
        parsedField['name'] = getCurrentTranslationFromI18N(value.name);
        parsedField['editHelp'] = getCurrentTranslationFromI18N(value.editHelp);

        if (parsedField.simplifiedReferenceMetadata) {
          parsedField.simplifiedReferenceMetadata.name = getCurrentTranslationFromI18N(
            parsedField.simplifiedReferenceMetadata.name,
          );
        }

        delete parsedField.displayOnEditMode;
        delete parsedField.transient;
        delete parsedField.unique;
        delete parsedField.valueExpression;
        delete parsedField.calculated;
        delete parsedField.engine;
        if (parsedField.additionalConfigs) {
          parsedField.additionalConfigs.placeholder = getCurrentTranslationFromI18N(
            parsedField.additionalConfigs.placeholder,
          );
          delete parsedField.additionalConfigs._class;
          delete parsedField.additionalConfigs._classRevision;
          delete parsedField.additionalConfigs.indexLevel;
          delete parsedField.additionalConfigs.onDelete;
          delete parsedField.additionalConfigs._id;
        }
        if (parsedField.exhibitionConfigs) {
          delete parsedField.exhibitionConfigs._class;
          delete parsedField.exhibitionConfigs._classRevision;
          delete parsedField.exhibitionConfigs._id;
        }
        if (parsedField.valueOptions?.length) {
          parsedField.valueOptions = parsedField.valueOptions.map((v) => {
            return { identifier: v.identifier, value: getCurrentTranslationFromI18N(v.value) };
          });
        }
        return parsedField;
      },
    },
  );
}
/**
 *  Helper Functions
 */

function getCurrentTranslationFromI18N(i18nJsObject) {
  return getLanguageValue(i18nJsObject, language, languagePriorityOrder, true);
}

function getMetadataPathFromReference(reference, option) {
  const _classId = reference._classId;
  const _id = reference._id;
  if (option && option !== 'CUSTOM') {
    return _classId + REFERENCE_SEPARATOR + _id + '[' + option + ']';
  }
  return _classId + REFERENCE_SEPARATOR + _id;
}

function getPathFromFieldAttribute(field, reference) {
  return getPathFromReference(reference) + (field ? ENTITY_IDENTIFIER_SEPARATOR + field : '');
}

function getPathFromReference(reference) {
  const _classId = reference._classId;
  const _id = reference._id;
  return _classId + REFERENCE_SEPARATOR + _id;
}

function getFieldAttributeFromPath(fieldPath) {
  const fieldAttribute = fieldPath.split(ENTITY_IDENTIFIER_SEPARATOR);
  fieldAttribute.shift();
  return fieldAttribute.join(ENTITY_IDENTIFIER_SEPARATOR);
}

function getReferenceFromPath(path) {
  const reg = new RegExp('(^\\w{24}' + REFERENCE_SEPARATOR + '\\w{24})');
  const match = path.match(reg);
  const refPath = match ? match[1] : null;
  if (!refPath) return null;

  const parts = refPath.split(REFERENCE_SEPARATOR);
  const _classId = parts[0];
  const _id = parts[1];
  return { _classId: _classId, _id: _id };
}

function parseFields(fields, parentPath, parentField) {
  const parsedFields = [];
  (fields || []).forEach((f) => {
    const entityData = {};
    entityData['name'] = f.name;
    entityData['nameCurrent'] = f.name;

    entityData[ENTITY_PARENT_IDENTIFIER_ATTRIBUTE] = parentField;
    const __path = (parentPath ? parentPath + ENTITY_IDENTIFIER_SEPARATOR : '') + f.identifier;
    entityData[ENTITY_IDENTIFIER_ATTRIBUTE] = __path;

    const parsed = Object.assign({}, f, entityData);
    parsedFields.push(parsed);
  });

  return parsedFields;

  /**
   * The section below was commented out due to an exception.
   * In the context of a form, when there is a decimal field of type "Dynamic Currency" and the currency field is hidden,
   * the helper cannot obtain the currencyId because the currency field is not included in parsedFields as it is hidden.
   */

  // const isParentEclass = parentPath.substring(parentPath.length - 6) === 'eClass';

  // if (isParentEclass) return parsedFields;

  // const visibleFields = parsedFields.filter((f) => f.hidden === false);

  // return visibleFields;
}

function checkIfPathIsSubPath(filteredFieldPath, path) {
  const regex = new RegExp('^' + filteredFieldPath + '(\\..+)?$');
  return regex.test(path);
}

function parseEmbbededMetadatas(metadatas, field) {
  metadatas = metadatas ? Object.assign({}, metadatas) : {};
  Object.keys(metadatas).forEach((k) => {
    metadatas[k] = parseEmbbededMetadata(metadatas[k], field, k);
  });
  return metadatas;
}

function parseEmbbededMetadata(metadata, field, key) {
  const entityData = {
    __field: field,
  };
  entityData[ENTITY_IDENTIFIER_ATTRIBUTE] = field + (key ? ENTITY_IDENTIFIER_SEPARATOR + key : '');

  return metadata ? Object.assign({}, metadata, entityData) : null;
}
